import {
    LoyaltyMembershipState,
    PaymentRequestState,
    ResourceBlockType,
    ResourceState,
} from '@mews-commander/data-access';
import {
    ReservationTimeState,
    ResourceState as ResourceStateLegacy,
    TemporalTaskState,
} from '@mews-commander/legacy-data-structures';
import { createHandler } from '@mews/std';
import { alpha, getToken, TagVariant } from '@optimus-web/core';
import { DENSE_B2B_LIGHT_THEME } from '@optimus-web/theme';
import { isOnlineCheckedIn } from './isOnlineCheckedIn';
import { isOnlineCheckedOut } from './isOnlineCheckedOut';

export const getColorFromResourceState = createHandler(
    {
        [ResourceStateLegacy.Dirty.value]: getToken('legacyTagBorderTangerine')({ theme: DENSE_B2B_LIGHT_THEME }),
        [ResourceStateLegacy.Clean.value]: getToken('legacyTagBorderDenim')({ theme: DENSE_B2B_LIGHT_THEME }),
        [ResourceStateLegacy.OutOfService.value]: getToken('legacyTagBorderRuby')({ theme: DENSE_B2B_LIGHT_THEME }),
        [ResourceStateLegacy.OutOfOrder.value]: getToken('legacyTagBorderMustard')({ theme: DENSE_B2B_LIGHT_THEME }),
        [ResourceStateLegacy.Inspected.value]: getToken('legacyTagBorderLime')({ theme: DENSE_B2B_LIGHT_THEME }),
    },
    getToken('paletteBrandNight600')({ theme: DENSE_B2B_LIGHT_THEME })
);

export const getTagVariantFromResourceState = createHandler<ResourceState, TagVariant>(
    {
        [ResourceState.Dirty]: 'tangerine',
        [ResourceState.Clean]: 'denim',
        [ResourceState.OutOfService]: 'ruby',
        [ResourceState.OutOfOrder]: 'mustard',
        [ResourceState.Inspected]: 'lime',
    },
    'basicBold'
);

export const getTagVariantFromTemporalTaskState = createHandler<string, TagVariant>(
    {
        [TemporalTaskState.Future.value]: 'basicBold',
        [TemporalTaskState.Pending.value]: 'denim',
        [TemporalTaskState.Missed.value]: 'tangerine',
        [TemporalTaskState.Closed.value]: 'basicBold',
    },
    'basicBold'
);

export const getTagVariantFromPaymentRequestState = createHandler<PaymentRequestState, TagVariant>(
    {
        [PaymentRequestState.Canceled]: 'basicBold',
        [PaymentRequestState.Pending]: 'primary',
        [PaymentRequestState.Expired]: 'warning',
        [PaymentRequestState.Completed]: 'success',
        [PaymentRequestState.WithTheBank]: 'primary',
    },
    'basicBold'
);

export const getTagVariantFromReservationTimeState = createHandler<
    number,
    TagVariant | ((reservationOptions: $TSFixMe) => TagVariant)
>(
    {
        [ReservationTimeState.Past.value]: 'basicSubtle',
        [ReservationTimeState.CheckOutMissed.value]: 'tangerine',
        [ReservationTimeState.ToBeCheckedOut.value]: (reservationOptions) =>
            isOnlineCheckedOut(reservationOptions) ? 'lime' : 'denim',
        [ReservationTimeState.CheckedIn.value]: 'basicBold',
        [ReservationTimeState.CheckInMissed.value]: 'tangerine',
        [ReservationTimeState.ToBeCheckedIn.value]: ({ companionExtent, ...reservationOptions }) =>
            isOnlineCheckedIn(companionExtent, reservationOptions) ? 'lime' : 'denim',
        [ReservationTimeState.Future.value]: 'basicSubtle',
        [ReservationTimeState.ConfirmationMissed.value]: 'tangerine',
    },
    ({ type }) => {
        if (type === ResourceBlockType.OutOfOrder) {
            return 'mustard';
        } else if (type === ResourceBlockType.InternalUse) {
            return 'lavender';
        }
        return 'basicSubtle';
    }
);
export const getColorFromReservationTimeState = createHandler(
    {
        [ReservationTimeState.Past.value]: {
            default: alpha(
                getToken('paletteBrandNight400')({ theme: DENSE_B2B_LIGHT_THEME }),
                getToken('opacity100')({ theme: DENSE_B2B_LIGHT_THEME })
            ),
            active: getToken('paletteBrandNight200')({ theme: DENSE_B2B_LIGHT_THEME }),
        },
        [ReservationTimeState.CheckOutMissed.value]: {
            default: alpha(
                getToken('legacyTagBorderTangerine')({ theme: DENSE_B2B_LIGHT_THEME }),
                getToken('opacity600')({ theme: DENSE_B2B_LIGHT_THEME })
            ),
            active: getToken('legacyTagBorderTangerine')({ theme: DENSE_B2B_LIGHT_THEME }),
        },
        [ReservationTimeState.ToBeCheckedOut.value]: (reservationOptions: $TSFixMe) =>
            isOnlineCheckedOut(reservationOptions)
                ? {
                      default: alpha(
                          getToken('legacyTagBorderLime')({ theme: DENSE_B2B_LIGHT_THEME }),
                          getToken('opacity600')({ theme: DENSE_B2B_LIGHT_THEME })
                      ),
                      active: getToken('legacyTagBorderLime')({ theme: DENSE_B2B_LIGHT_THEME }),
                  }
                : {
                      default: alpha(
                          getToken('legacyTagBorderDenim')({ theme: DENSE_B2B_LIGHT_THEME }),
                          getToken('opacity600')({ theme: DENSE_B2B_LIGHT_THEME })
                      ),
                      active: getToken('legacyTagBorderDenim')({ theme: DENSE_B2B_LIGHT_THEME }),
                  },
        [ReservationTimeState.CheckedIn.value]: {
            default: alpha(
                getToken('paletteBrandNight400')({ theme: DENSE_B2B_LIGHT_THEME }),
                getToken('opacity400')({ theme: DENSE_B2B_LIGHT_THEME })
            ),
            active: getToken('paletteBrandNight600')({ theme: DENSE_B2B_LIGHT_THEME }),
        },
        [ReservationTimeState.CheckInMissed.value]: {
            default: alpha(
                getToken('legacyTagBorderTangerine')({ theme: DENSE_B2B_LIGHT_THEME }),
                getToken('opacity600')({ theme: DENSE_B2B_LIGHT_THEME })
            ),
            active: getToken('legacyTagBorderTangerine')({ theme: DENSE_B2B_LIGHT_THEME }),
        },
        [ReservationTimeState.ToBeCheckedIn.value]: ({ companionExtent, ...reservationOptions }: $TSFixMe) =>
            isOnlineCheckedIn(companionExtent, reservationOptions)
                ? {
                      default: alpha(
                          getToken('legacyTagBorderLime')({ theme: DENSE_B2B_LIGHT_THEME }),
                          getToken('opacity600')({ theme: DENSE_B2B_LIGHT_THEME })
                      ),
                      active: getToken('legacyTagBorderLime')({ theme: DENSE_B2B_LIGHT_THEME }),
                  }
                : {
                      default: alpha(
                          getToken('legacyTagBorderDenim')({ theme: DENSE_B2B_LIGHT_THEME }),
                          getToken('opacity600')({ theme: DENSE_B2B_LIGHT_THEME })
                      ),
                      active: getToken('legacyTagBorderDenim')({ theme: DENSE_B2B_LIGHT_THEME }),
                  },
        [ReservationTimeState.Future.value]: {
            default: alpha(
                getToken('paletteBrandNight400')({ theme: DENSE_B2B_LIGHT_THEME }),
                getToken('opacity100')({ theme: DENSE_B2B_LIGHT_THEME })
            ),
            active: getToken('paletteBrandNight200')({ theme: DENSE_B2B_LIGHT_THEME }),
        },
        [ReservationTimeState.ConfirmationMissed.value]: {
            default: alpha(
                getToken('legacyTagBorderTangerine')({ theme: DENSE_B2B_LIGHT_THEME }),
                getToken('opacity600')({ theme: DENSE_B2B_LIGHT_THEME })
            ),
            active: getToken('legacyTagBorderTangerine')({ theme: DENSE_B2B_LIGHT_THEME }),
        },
    },
    ({ type }) => {
        if (type === ResourceBlockType.OutOfOrder) {
            return {
                default: alpha(
                    getToken('legacyTagBorderMustard')({ theme: DENSE_B2B_LIGHT_THEME }),
                    getToken('opacity600')({ theme: DENSE_B2B_LIGHT_THEME })
                ),
                active: getToken('legacyTagBorderMustard')({ theme: DENSE_B2B_LIGHT_THEME }),
            };
        } else if (type === ResourceBlockType.InternalUse) {
            return {
                default: alpha(
                    getToken('legacyTagBorderLavender')({ theme: DENSE_B2B_LIGHT_THEME }),
                    getToken('opacity600')({ theme: DENSE_B2B_LIGHT_THEME })
                ),
                active: getToken('legacyTagBorderLavender')({ theme: DENSE_B2B_LIGHT_THEME }),
            };
        }
        return {
            default: alpha(
                getToken('paletteBrandNight400')({ theme: DENSE_B2B_LIGHT_THEME }),
                getToken('opacity100')({ theme: DENSE_B2B_LIGHT_THEME })
            ),
            active: getToken('paletteBrandNight200')({ theme: DENSE_B2B_LIGHT_THEME }),
        };
    }
);

export const getStrokeColorFromReservationTimeState = createHandler(
    {
        [ReservationTimeState.Past.value]: getToken('paletteBrandNight600')({ theme: DENSE_B2B_LIGHT_THEME }),
        [ReservationTimeState.CheckOutMissed.value]: getToken('legacyTagBorderTangerine')({
            theme: DENSE_B2B_LIGHT_THEME,
        }),
        [ReservationTimeState.ToBeCheckedOut.value]: (reservationOptions: $TSFixMe) =>
            isOnlineCheckedOut(reservationOptions)
                ? getToken('legacyTagBorderLime')({ theme: DENSE_B2B_LIGHT_THEME })
                : getToken('legacyTagBorderDenim')({ theme: DENSE_B2B_LIGHT_THEME }),
        [ReservationTimeState.CheckedIn.value]: getToken('paletteBrandNight600')({ theme: DENSE_B2B_LIGHT_THEME }),
        [ReservationTimeState.CheckInMissed.value]: getToken('legacyTagBorderTangerine')({
            theme: DENSE_B2B_LIGHT_THEME,
        }),
        [ReservationTimeState.ToBeCheckedIn.value]: ({ companionExtent, ...reservationOptions }: $TSFixMe) =>
            isOnlineCheckedIn(companionExtent, reservationOptions)
                ? getToken('legacyTagBorderLime')({ theme: DENSE_B2B_LIGHT_THEME })
                : getToken('legacyTagBorderDenim')({ theme: DENSE_B2B_LIGHT_THEME }),
        [ReservationTimeState.Future.value]: getToken('paletteBrandNight600')({ theme: DENSE_B2B_LIGHT_THEME }),
        [ReservationTimeState.ConfirmationMissed.value]: getToken('legacyTagBorderTangerine')({
            theme: DENSE_B2B_LIGHT_THEME,
        }),
    },

    ({ type }: $TSFixMe) => {
        if (type === ResourceBlockType.OutOfOrder) {
            return getToken('legacyTagBorderMustard')({ theme: DENSE_B2B_LIGHT_THEME });
        } else if (type === ResourceBlockType.InternalUse) {
            return getToken('legacyTagBorderLavender')({ theme: DENSE_B2B_LIGHT_THEME });
        }
        return getToken('paletteBrandNight600')({ theme: DENSE_B2B_LIGHT_THEME });
    }
);

export const getTextColorFromReservationTimeState = createHandler(
    {
        [ReservationTimeState.Past.value]: getToken('paletteBrandNight900')({ theme: DENSE_B2B_LIGHT_THEME }),
        [ReservationTimeState.CheckOutMissed.value]: getToken('paletteBrandNight900')({ theme: DENSE_B2B_LIGHT_THEME }),
        [ReservationTimeState.ToBeCheckedOut.value]: (reservationOptions: $TSFixMe) =>
            isOnlineCheckedOut(reservationOptions)
                ? getToken('paletteBrandNight0')({ theme: DENSE_B2B_LIGHT_THEME })
                : getToken('paletteBrandNight0')({ theme: DENSE_B2B_LIGHT_THEME }),
        [ReservationTimeState.CheckedIn.value]: getToken('paletteBrandNight0')({ theme: DENSE_B2B_LIGHT_THEME }),
        [ReservationTimeState.CheckInMissed.value]: getToken('paletteBrandNight900')({ theme: DENSE_B2B_LIGHT_THEME }),
        [ReservationTimeState.ToBeCheckedIn.value]: ({ companionExtent, ...reservationOptions }: $TSFixMe) =>
            isOnlineCheckedIn(companionExtent, reservationOptions)
                ? getToken('paletteBrandNight0')({ theme: DENSE_B2B_LIGHT_THEME })
                : getToken('paletteBrandNight0')({ theme: DENSE_B2B_LIGHT_THEME }),
        [ReservationTimeState.Future.value]: getToken('paletteBrandNight900')({ theme: DENSE_B2B_LIGHT_THEME }),
        [ReservationTimeState.ConfirmationMissed.value]: getToken('paletteBrandNight900')({
            theme: DENSE_B2B_LIGHT_THEME,
        }),
    },
    ({ type }) => {
        const outOfOrderColor = getToken('paletteBrandNight900')({ theme: DENSE_B2B_LIGHT_THEME });
        return type === ResourceBlockType.OutOfOrder
            ? outOfOrderColor
            : getToken('paletteBrandNight0')({ theme: DENSE_B2B_LIGHT_THEME });
    }
);

export const getAlphaFromReservationTimeState = createHandler(
    {
        [ReservationTimeState.CheckedIn.value]: 0.3,
    },
    0.1
);

export const getTagVariantFromLoyaltyMembershipState = createHandler<LoyaltyMembershipState, TagVariant>(
    {
        [LoyaltyMembershipState.New]: 'primary',
        [LoyaltyMembershipState.Pending]: 'info',
        [LoyaltyMembershipState.Enrolled]: 'success',
        [LoyaltyMembershipState.Declined]: 'danger',
        [LoyaltyMembershipState.Canceled]: 'warning',
    },
    'basicBold'
);
